import { create } from "zustand";

export type NotificationVariant = "success" | "error" | "warning" | "default";

/**
 * Strips HTTP status codes, server internals, and technical noise from error
 * messages before they reach the user-facing notification bar.
 */
function sanitizeErrorForDisplay(raw: string): string {
  // Strip leading HTTP status patterns like "401", "Request failed with status code 500", etc.
  const msg = raw
    .replace(/^(Request failed with status code \d+)\s*/i, "")
    .replace(/^\d{3}\s*[-:.]?\s*/i, "")
    .replace(
      /^(Bad Request|Unauthorized|Forbidden|Not Found|Conflict|Internal Server Error)\s*[-:.]?\s*/i,
      "",
    )
    .trim();

  // If stripping left us with nothing meaningful, use a generic fallback
  if (!msg || msg.length < 3) {
    return "Something went wrong. Please try again.";
  }

  return msg;
}

export interface NotificationEntry {
  id: string;
  message: string;
  variant: NotificationVariant;
  action?: { label: string; onClick: () => void };
  progress?: number;
  /// Stays until replaced or dismissed; the renderer adds a Dismiss.
  persistent?: boolean;
  /// Work with no known fraction: spinner instead of the glyph, and persistent.
  busy?: boolean;
}

interface NotificationStore {
  current: NotificationEntry | null;
  /// Object form of `show`, for entries that need the flags above (`show`'s
  /// positional signature is what the WASM exports call).
  post: (entry: Omit<NotificationEntry, "id">) => void;
  show: (
    message: string,
    variant?: NotificationVariant,
    action?: { label: string; onClick: () => void },
    progress?: number,
  ) => void;
  updateProgress: (progress: number) => void;
  dismiss: () => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  current: null,

  post: (entry) =>
    set({
      current: {
        ...entry,
        message:
          entry.variant === "error"
            ? sanitizeErrorForDisplay(entry.message)
            : entry.message,
        id: `${Date.now()}-${Math.random()}`,
      },
    }),

  show: (message, variant = "default", action, progress) => {
    const displayMessage =
      variant === "error" ? sanitizeErrorForDisplay(message) : message;
    set({
      current: {
        id: `${Date.now()}-${Math.random()}`,
        message: displayMessage,
        variant,
        action,
        progress,
      },
    });
  },

  updateProgress: (progress: number) => {
    const current = get().current;
    if (current && current.progress !== undefined) {
      set({ current: { ...current, progress } });
    }
  },

  dismiss: () => {
    set({ current: null });
  },
}));

// Global accessors for non-React contexts (e.g., WASM FFI exports.tsx)
export const showNotificationGlobal = (
  message: string,
  variant?: NotificationVariant,
  action?: { label: string; onClick: () => void },
  progress?: number,
) => useNotificationStore.getState().show(message, variant, action, progress);

export const updateNotificationProgressGlobal = (progress: number) =>
  useNotificationStore.getState().updateProgress(progress);
